import React, { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import { usePageState } from "../context/AppStateContext";
import { useMessageBox } from "../components/Common/MessageBox";
import "./GeneralSettingPage.css";
import { MDBRow, MDBCol, MDBInput, MDBBtn, MDBIcon } from "mdb-react-ui-kit";

// DIFFERENT OPTIONS FOR DESCRIPTION
const DESCRIPTION_STYLES = [
  {
    value: "ai_seo_friendly_desc",
    label: "AI SEO Friendly Description",
    hint: "Will include your store URL and location in description",
  },
  {
    value: "html_ai_seo_desc",
    label: "HTML AI SEO Description",
    hint: "Will include SKU in HTML formatted description",
  },
  {
    value: "text_formatted_desc",
    label: "Text Formatted Description",
    hint: "Paragraph text then product information",
  },
  {
    value: "html_formatted_desc",
    label: "HTML Formatted Description",
    hint: "HTML paragraphs with product details",
  },
  {
    value: "custom_desc",
    label: "Custom Description",
    hint: "Your own description template",
  },
];

// DIFFERENT OPTIONS FOR TITLE
const TITLE_STYLES = [
  {
    value: "ai_seo_friendly_title",
    label: "AI SEO Friendly Title",
    hint: "SEO optimized title with keywords",
  },
  {
    value: "html_ai_seo_title",
    label: "HTML AI SEO Title",
    hint: "HTML title with SKU",
  },
  {
    value: "text_formatted_title",
    label: "Text Formatted Title",
    hint: "Simple formatted title text",
  },
  {
    value: "html_formatted_title",
    label: "HTML Formatted Title",
    hint: "Title with HTML formatting",
  },
  {
    value: "custom_title",
    label: "Custom Title",
    hint: "Your own title template",
  },
];

// DIFFERENT OPTIONS FOR TAGS
const TAGS_STYLES = [
  {
    value: "ai_seo_friendly_tags",
    label: "AI SEO Friendly Tags",
    hint: "AI generated SEO optimized tags",
  },
  {
    value: "category_based_tags",
    label: "Category Based Tags",
    hint: "Tags based on product categories",
  },
  {
    value: "keyword_tags",
    label: "Keyword Based Tags",
    hint: "Tags extracted from product keywords",
  },
  {
    value: "custom_tags",
    label: "Custom Tags",
    hint: "Your own tag template",
  },
];

const GeneralSettingPage = () => {
  const { setState: setPersistedState } = usePageState("generalSettings");
  const { showError, showSuccess, showWarning, MessageBoxComponent } =
    useMessageBox();

  // Initialize settings
  const [settings, setSettings] = useState({
    username: "",
    companyName: "",
    currency: "",
    skuFormat: "",
    descriptionStyle: "text_formatted_desc",
    descriptionPrompt: "",
    titleStyle: "text_formatted_title",
    titlePrompt: "",
    tagsStyle: "category_based_tags",
    tagsPrompt: "",
  });

  const [vendors, setVendors] = useState([]);
  const [newVendor, setNewVendor] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const { companyId } = useAuth();

  // ================= Parse combined value helper =================
  const parseCombinedValue = useCallback((combinedValue) => {
    console.log("🔄 Parsing combined value:", combinedValue);
    
    if (!combinedValue || combinedValue === '') {
      return { style: '', prompt: '' };
    }

    if (combinedValue.includes("|||")) {
      const parts = combinedValue.split("|||");
      return {
        style: parts[0] || '',
        prompt: parts[1] || '',
      };
    }

    return {
      style: combinedValue,
      prompt: '',
    };
  }, []);

  // ================= Get placeholders =================
  const getDescriptionPlaceholder = useCallback(() => {
    switch (settings.descriptionStyle) {
      case "ai_seo_friendly_desc":
        return "AI will add store URL and location automatically. Add any specific keywords...";
      case "html_ai_seo_desc":
        return "HTML description with SKU. Example: <p>Product details with SKU: [SKU]</p>";
      case "text_formatted_desc":
        return "Start with paragraph about product, then list features...";
      case "html_formatted_desc":
        return "HTML format. Example: <p>Product description</p><p>Color variations...</p>";
      case "custom_desc":
        return "Enter your custom description template. Use placeholders...";
      default:
        return "Enter description pattern...";
    }
  }, [settings.descriptionStyle]);

  const getTitlePlaceholder = useCallback(() => {
    switch (settings.titleStyle) {
      case "ai_seo_friendly_title":
        return "AI optimized title. Add keywords for SEO...";
      case "html_ai_seo_title":
        return "HTML title with SKU. Example: <strong>Product Name</strong> - SKU: [SKU]";
      case "text_formatted_title":
        return "Simple title format. Example: Brand | Model | Color";
      case "html_formatted_title":
        return "HTML title. Example: <h1>Product Name</h1>";
      case "custom_title":
        return "Enter your custom title template...";
      default:
        return "Enter title pattern...";
    }
  }, [settings.titleStyle]);

  const getTagsPlaceholder = useCallback(() => {
    switch (settings.tagsStyle) {
      case "ai_seo_friendly_tags":
        return "AI will generate SEO friendly tags based on product description...";
      case "category_based_tags":
        return "Tags will be generated based on product category and subcategories...";
      case "keyword_tags":
        return "Enter keywords separated by commas...";
      case "custom_tags":
        return "Enter custom tag template...";
      default:
        return "Enter tag generation instructions...";
    }
  }, [settings.tagsStyle]);

  // ================= Fetch settings =================
  useEffect(() => {
    const fetchSettings = async () => {
      if (!companyId || hasFetched) {
        return;
      }

      try {
        console.log("🔍 Fetching settings for company:", companyId);
        const res = await api.get(`/api/general-settings/${companyId}`);

        const data = res.data || {};
        console.log("📥 Fetched data from API:", data);

        const descriptionParsed = parseCombinedValue(data.description_style);
        const titleParsed = parseCombinedValue(data.title_style);
        const tagsParsed = parseCombinedValue(data.tags_style);

        setSettings({
          username: data.username || "",
          companyName: data.company_name || "",
          currency: data.currency || "",
          skuFormat: data.sku_format || "",
          descriptionStyle: descriptionParsed.style || "text_formatted_desc",
          descriptionPrompt: descriptionParsed.prompt || "",
          titleStyle: titleParsed.style || "text_formatted_title",
          titlePrompt: titleParsed.prompt || "",
          tagsStyle: tagsParsed.style || "category_based_tags",
          tagsPrompt: tagsParsed.prompt || "",
        });

        const vendorArray = data.vendor_list
          ? data.vendor_list.split(",").filter((v) => v.trim() !== "")
          : [];
        setVendors(vendorArray);

        setHasFetched(true);
        setLoading(false);
      } catch (err) {
        console.error("❌ Failed to fetch settings:", err);
        showError("Failed to fetch settings. Please try again.");
        setLoading(false);
      }
    };

    fetchSettings();
  }, [companyId, parseCombinedValue, hasFetched, showError]);

  // ================= Sync to context =================
  useEffect(() => {
    if (!hasFetched) return;
    
    const { username, companyName, ...persistableSettings } = settings;
    setPersistedState({
      settings: persistableSettings,
      vendors,
      newVendor,
    });
  }, [settings, vendors, newVendor, setPersistedState, hasFetched]);

  // ================= Event Handlers =================
  const handleChange = useCallback((key, value) => {
    console.log(`✏️ Changing ${key}:`, value);
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  const addVendor = useCallback(() => {
    const trimmed = newVendor.trim();
    if (!trimmed) {
      showWarning("Please enter vendor name");
      return;
    }
    if (vendors.includes(trimmed)) {
      showWarning("Vendor already exists in the list");
      return;
    }
    setVendors((prev) => [...prev, trimmed]);
    setNewVendor("");
  }, [newVendor, vendors, showWarning]);

  const removeVendor = useCallback((index) => {
    setVendors((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // ================= Save Settings =================
  const handleSave = async () => {
    if (!companyId) {
      showError("Company ID is missing. Please refresh the page.");
      return;
    }

    setSaving(true);
    
    try {
      const descriptionCombined = `${settings.descriptionStyle}|||${settings.descriptionPrompt}`;
      const titleCombined = `${settings.titleStyle}|||${settings.titlePrompt}`;
      const tagsCombined = `${settings.tagsStyle}|||${settings.tagsPrompt}`;

      console.log("📤 Saving payload:", {
        description: descriptionCombined,
        title: titleCombined,
        tags: tagsCombined
      });

      const payload = {
        currency: settings.currency,
        sku_format: settings.skuFormat,
        vendor_list: vendors.join(","),
        description_style: descriptionCombined,
        title_style: titleCombined,
        tags_style: tagsCombined,
      };

      await api.put(`/api/general-settings/${companyId}`, payload);
      showSuccess("Settings saved successfully!");
      
    } catch (err) {
      console.error("❌ Save error:", err);
      showError(err.response?.data?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <h1 className="page-title">Account Settings</h1>

      {/* Account Info */}
      <div className="settings-card">
        <h4><MDBIcon icon="user" className="me-2" />Account Info</h4>
        <MDBRow>
          <MDBCol md="6">
            <MDBInput 
              label="Username" 
              value={settings.username} 
              disabled 
              className="mb-3"
            />
          </MDBCol>
          <MDBCol md="6">
            <MDBInput
              label="Company Name"
              value={settings.companyName}
              onChange={(e) => handleChange("companyName", e.target.value)}
              className="mb-3"
            />
          </MDBCol>
        </MDBRow>
      </div>

      {/* Listing Settings */}
      <div className="settings-card">
        <h4><MDBIcon icon="cog" className="me-2" />Listing Settings</h4>
        <MDBRow>
          <MDBCol md="6">
            <MDBInput
              label="Currency"
              value={settings.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
              placeholder="e.g., USD, EUR, GBP"
              className="mb-3"
            />
          </MDBCol>
          <MDBCol md="6">
            <MDBInput
              label="SKU Format"
              value={settings.skuFormat}
              onChange={(e) => handleChange("skuFormat", e.target.value)}
              placeholder="e.g., BRAND-MODEL-COLOR"
              className="mb-3"
            />
          </MDBCol>
        </MDBRow>
      </div>

      {/* Vendors */}
      <div className="settings-card">
        <h4><MDBIcon icon="store" className="me-2" />Vendor List</h4>
        <div className="vendor-input-group">
          <MDBInput
            label="Add Vendor"
            value={newVendor}
            onChange={(e) => setNewVendor(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addVendor();
              }
            }}
            className="mb-2"
          />
          <MDBBtn color="primary" onClick={addVendor} className="mb-3">
            <MDBIcon icon="plus" className="me-1" />
            Add Vendor
          </MDBBtn>
        </div>

        {vendors.length > 0 ? (
          <div className="vendor-list mt-3">
            {vendors.map((vendor, index) => (
              <div
                key={index}
                className="vendor-item d-flex justify-content-between align-items-center p-2 mb-2 border rounded"
              >
                <span className="vendor-name">{vendor}</span>
                <MDBBtn
                  size="sm"
                  color="danger"
                  onClick={() => removeVendor(index)}
                  className="vendor-remove-btn"
                >
                  <MDBIcon icon="trash" />
                </MDBBtn>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted mt-3">No vendors added yet.</p>
        )}
      </div>

      {/* DESCRIPTION SETTINGS */}
      <div className="settings-card">
        <h4><MDBIcon icon="file-alt" className="me-2" />Description Generation</h4>

        <div className="mb-3">
          <label className="form-label fw-bold">Description Style</label>
          <select
            className="form-select"
            value={settings.descriptionStyle}
            onChange={(e) => handleChange("descriptionStyle", e.target.value)}
          >
            {DESCRIPTION_STYLES.map((opt) => (
              <option key={`desc-${opt.value}`} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className="form-text text-muted">
            {DESCRIPTION_STYLES.find(opt => opt.value === settings.descriptionStyle)?.hint}
          </div>
        </div>

        <div className="mt-3">
          <label className="form-label fw-bold">Description Prompt</label>
          <textarea
            className="form-control"
            rows={3}
            placeholder={getDescriptionPlaceholder()}
            value={settings.descriptionPrompt}
            onChange={(e) => handleChange("descriptionPrompt", e.target.value)}
            style={{ resize: "vertical" }}
          />
          <div className="form-text text-muted">
            Customize description generation. Use placeholders like [PRODUCT_NAME], [SKU]
          </div>
        </div>
      </div>

      {/* TITLE SETTINGS */}
      <div className="settings-card">
        <h4><MDBIcon icon="heading" className="me-2" />Title Generation</h4>

        <div className="mb-3">
          <label className="form-label fw-bold">Title Style</label>
          <select
            className="form-select"
            value={settings.titleStyle}
            onChange={(e) => handleChange("titleStyle", e.target.value)}
          >
            {TITLE_STYLES.map((opt) => (
              <option key={`title-${opt.value}`} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className="form-text text-muted">
            {TITLE_STYLES.find(opt => opt.value === settings.titleStyle)?.hint}
          </div>
        </div>

        <div className="mt-3">
          <label className="form-label fw-bold">Title Prompt</label>
          <textarea
            className="form-control"
            rows={2}
            placeholder={getTitlePlaceholder()}
            value={settings.titlePrompt}
            onChange={(e) => handleChange("titlePrompt", e.target.value)}
            style={{ resize: "vertical" }}
          />
          <div className="form-text text-muted">
            Customize title generation. Keep it SEO friendly.
          </div>
        </div>
      </div>

      {/* TAGS SETTINGS */}
      <div className="settings-card">
        <h4><MDBIcon icon="tags" className="me-2" />Tags Generation</h4>

        <div className="mb-3">
          <label className="form-label fw-bold">Tags Style</label>
          <select
            className="form-select"
            value={settings.tagsStyle}
            onChange={(e) => handleChange("tagsStyle", e.target.value)}
          >
            {TAGS_STYLES.map((opt) => (
              <option key={`tags-${opt.value}`} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className="form-text text-muted">
            {TAGS_STYLES.find(opt => opt.value === settings.tagsStyle)?.hint}
          </div>
        </div>

        <div className="mt-3">
          <label className="form-label fw-bold">Tags Prompt</label>
          <textarea
            className="form-control"
            rows={2}
            placeholder={getTagsPlaceholder()}
            value={settings.tagsPrompt}
            onChange={(e) => handleChange("tagsPrompt", e.target.value)}
            style={{ resize: "vertical" }}
          />
          <div className="form-text text-muted">
            Customize tag generation. Separate multiple tags with commas.
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="save-section text-center mt-4 p-4 border-top">
        <MDBBtn
          color="success"
          size="lg"
          onClick={handleSave}
          disabled={saving}
          style={{ minWidth: "250px" }}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Saving...
            </>
          ) : (
            <>
              <MDBIcon icon="save" className="me-2" />
              Save All Settings
            </>
          )}
        </MDBBtn>
        <p className="text-muted mt-2">
          Your settings will be automatically applied to all new product listings.
        </p>
      </div>

      <MessageBoxComponent />
    </div>
  );
};

export default GeneralSettingPage;